/**
 * Per-day IndexedDB cache for workflow runs.
 *
 * Separate store from the react-query persister (gha-query-cache) so the
 * two layers stay conceptually independent: this one is keyed per
 * `<owner>-<repo>-<YYYY-MM-DD>` and exists so an interrupted fetch can
 * resume without redoing completed days. The react-query persister keeps
 * caring about full-query hydration for instant reloads.
 *
 * TTL shares BUN_PUBLIC_CACHE_TTL_MINUTES with the main persister for
 * consistency. A stale day is fetched again; fresh entries short-circuit
 * the API call.
 */

import { clear, createStore, entries, get, set } from "idb-keyval";
import type { WorkflowRun } from "@/lib/github/types";
import { saveRecentRepos } from "@/lib/recent-repos";

const DEFAULT_TTL_MIN = 15;
const rawTtl = process.env.BUN_PUBLIC_CACHE_TTL_MINUTES;
const parsedTtl = rawTtl ? Number.parseInt(rawTtl, 10) : NaN;
const TTL_MS =
  (Number.isFinite(parsedTtl) && parsedTtl > 0 ? parsedTtl : DEFAULT_TTL_MIN) * 60 * 1000;

type IdbStore = ReturnType<typeof createStore>;
// Separate DB from the react-query persister (`gha-cache`). idb-keyval opens
// each DB at version 1 without onupgradeneeded once it exists, so adding a
// second store to an existing DB fails silently — keeping the caches in
// distinct DBs sidesteps the upgrade dance entirely.
let dayStore: IdbStore | null = null;
try {
  dayStore = createStore("gha-day-cache", "days");
} catch (err) {
  if (import.meta.env.DEV) {
    console.warn("[day-cache] IndexedDB unavailable, day cache disabled", err);
  }
}

interface DayCacheEntry<T> {
  /** Unique items fetched for this day. */
  items: T[];
  /** True if this day's single-query already exceeded the API's 1000 cap. */
  hitCap: boolean;
  /** Epoch ms at which the entry was written. */
  fetchedAt: number;
}

function dayKey(owner: string, repo: string, isoDay: string): string {
  return `${owner.toLowerCase()}-${repo.toLowerCase()}-${isoDay}`;
}

/** Returns the cached entry if still within TTL, else null. */
export async function getFreshDay<T>(
  owner: string,
  repo: string,
  isoDay: string,
): Promise<DayCacheEntry<T> | null> {
  if (!dayStore) return null;
  try {
    const entry = await get<DayCacheEntry<T>>(dayKey(owner, repo, isoDay), dayStore);
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > TTL_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

/** Stores one day's worth of items. Swallows errors silently (quota, blocked). */
export async function setDay<T>(
  owner: string,
  repo: string,
  isoDay: string,
  items: T[],
  hitCap: boolean,
): Promise<void> {
  if (!dayStore) return;
  try {
    const entry: DayCacheEntry<T> = {
      items,
      hitCap,
      fetchedAt: Date.now(),
    };
    await set(dayKey(owner, repo, isoDay), entry, dayStore);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[day-cache] setDay failed", err);
    }
  }
}

/**
 * Wipe every day entry (used by signOut).
 * Safe to call even when IndexedDB is unavailable.
 */
export async function clearDayCache(): Promise<void> {
  if (!dayStore) return;
  try {
    await clear(dayStore);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[day-cache] clear failed", err);
    }
  }
}

// ───────────────────────── CSV export / import ─────────────────────────────
// Thin CSV: only the fields the analytics compute functions actually consume.
// Nested WorkflowRun shapes (actor, head_commit, repository metadata beyond
// owner/name) are dropped — imported data is "dashboard-ready" but not a
// forensic replica of the Octokit response. Users can always re-fetch to
// restore full fidelity.

const CSV_HEADERS = [
  "owner",
  "repo",
  "day",
  "id",
  "workflow_id",
  "name",
  "event",
  "head_branch",
  "head_sha",
  "conclusion",
  "status",
  "created_at",
  "run_started_at",
  "updated_at",
  "run_attempt",
  "html_url",
] as const;

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += c ?? "";
      }
    } else if (c === ",") {
      out.push(current);
      current = "";
    } else if (c === '"' && current === "") {
      inQuotes = true;
    } else {
      current += c ?? "";
    }
  }
  out.push(current);
  return out;
}

/** Best-effort accessor for a nested string without tripping over unknown shapes. */
function pickString(value: unknown, path: string[]): string {
  let cur: unknown = value;
  for (const key of path) {
    if (cur == null || typeof cur !== "object") return "";
    cur = (cur as Record<string, unknown>)[key];
  }
  return typeof cur === "string" ? cur : "";
}

/**
 * Serialise the entire day cache as CSV — one row per workflow run. Owner
 * and repo are read from `run.repository`, day from the first 10 chars of
 * `created_at`, so the key format remains free to change later.
 */
export async function exportDayCacheAsCSV(): Promise<string> {
  if (!dayStore) return CSV_HEADERS.join(",") + "\n";
  const all = await entries<string, DayCacheEntry<WorkflowRun>>(dayStore);
  const lines: string[] = [CSV_HEADERS.join(",")];
  for (const [, entry] of all) {
    for (const run of entry.items) {
      const owner = pickString(run, ["repository", "owner", "login"]);
      const repo = pickString(run, ["repository", "name"]);
      const day = typeof run.created_at === "string" ? run.created_at.slice(0, 10) : "";
      const row = [
        owner,
        repo,
        day,
        String(run.id ?? ""),
        String(run.workflow_id ?? ""),
        run.name ?? "",
        run.event ?? "",
        run.head_branch ?? "",
        run.head_sha ?? "",
        (run.conclusion as string | null) ?? "",
        run.status ?? "",
        run.created_at ?? "",
        run.run_started_at ?? "",
        run.updated_at ?? "",
        String(run.run_attempt ?? 1),
        run.html_url ?? "",
      ];
      lines.push(row.map(csvEscape).join(","));
    }
  }
  // Trailing newline so downstream tools (Excel, awk) don't choke on the last row.
  return lines.join("\n") + "\n";
}

export interface ImportResult {
  imported: number;
  days: number;
  skipped: number;
  /** Number of owner/repo slugs added to the picker's recent list. */
  reposAdded: number;
}

/**
 * Parse a previously-exported CSV and repopulate the day cache. Grouping is
 * by (owner, repo, day) — one IDB entry per group. Each imported entry is
 * stamped with a fresh `fetchedAt`, so TTL starts anew.
 */
export async function importDayCacheFromCSV(csv: string): Promise<ImportResult> {
  if (!dayStore) return { imported: 0, days: 0, skipped: 0, reposAdded: 0 };

  const lines = csv.split(/\r?\n/).filter((l) => l.length > 0);
  const headerLine = lines.shift();
  if (!headerLine) throw new Error("Empty CSV");

  const headers = parseCsvLine(headerLine);
  const idx: Record<(typeof CSV_HEADERS)[number], number> = {} as never;
  for (const name of CSV_HEADERS) {
    const i = headers.indexOf(name);
    if (i < 0) throw new Error(`Missing required CSV column: ${name}`);
    (idx as Record<string, number>)[name] = i;
  }

  const grouped = new Map<string, WorkflowRun[]>();
  let skipped = 0;
  for (const line of lines) {
    const cells = parseCsvLine(line);
    const owner = cells[idx.owner] ?? "";
    const repo = cells[idx.repo] ?? "";
    const day = cells[idx.day] ?? "";
    if (!owner || !repo || !day) {
      skipped++;
      continue;
    }
    // Reconstruct a WorkflowRun-shaped record with the cached fields. Cast is
    // a known limitation of the thin-CSV trade-off — see CSV_HEADERS comment.
    const run = {
      id: Number(cells[idx.id] ?? 0),
      workflow_id: Number(cells[idx.workflow_id] ?? 0),
      name: cells[idx.name] || null,
      event: cells[idx.event] ?? "",
      head_branch: cells[idx.head_branch] || null,
      head_sha: cells[idx.head_sha] ?? "",
      conclusion: cells[idx.conclusion] || null,
      status: cells[idx.status] || null,
      created_at: cells[idx.created_at] ?? "",
      run_started_at: cells[idx.run_started_at] || null,
      updated_at: cells[idx.updated_at] ?? "",
      run_attempt: Number(cells[idx.run_attempt] ?? 1),
      html_url: cells[idx.html_url] ?? "",
      // Inject a minimal repository shape so future exports round-trip owner/repo.
      repository: { owner: { login: owner }, name: repo },
    } as unknown as WorkflowRun;

    const groupKey = `${owner}|${repo}|${day}`;
    const list = grouped.get(groupKey);
    if (list) list.push(run);
    else grouped.set(groupKey, [run]);
  }

  // Compute the (owner, repo) → [minDay, maxDay] range so we can fill in
  // empty-day entries. Without this, a day with zero runs in the CSV has no
  // IDB entry, and paginateWithTimeSlice Step 0 ("all days cached") fails,
  // falling back to a full-window refetch even for windows fully inside the
  // imported range.
  const ranges = new Map<string, { minDay: string; maxDay: string }>();
  for (const groupKey of grouped.keys()) {
    const parts = groupKey.split("|");
    const owner = parts[0] ?? "";
    const repo = parts[1] ?? "";
    const day = parts[2] ?? "";
    if (!owner || !repo || !day) continue;
    const rangeKey = `${owner}|${repo}`;
    const cur = ranges.get(rangeKey);
    if (!cur) {
      ranges.set(rangeKey, { minDay: day, maxDay: day });
    } else {
      if (day < cur.minDay) cur.minDay = day;
      if (day > cur.maxDay) cur.maxDay = day;
    }
  }

  let imported = 0;
  const writtenDays = new Set<string>();
  for (const [groupKey, items] of grouped) {
    const parts = groupKey.split("|");
    const owner = parts[0] ?? "";
    const repo = parts[1] ?? "";
    const day = parts[2] ?? "";
    if (!owner || !repo || !day) {
      skipped += items.length;
      continue;
    }
    await setDay(owner, repo, day, items, false);
    writtenDays.add(groupKey);
    imported += items.length;
  }

  // Fill in empty-day entries for any missing day in each (owner, repo)
  // range so paginator Step 0 short-circuits for windows fully inside the
  // imported span — avoiding a full-window refetch after import.
  let filled = 0;
  for (const [rangeKey, { minDay, maxDay }] of ranges) {
    const parts = rangeKey.split("|");
    const owner = parts[0] ?? "";
    const repo = parts[1] ?? "";
    if (!owner || !repo) continue;
    for (const day of enumerateIsoDays(minDay, maxDay)) {
      const key = `${owner}|${repo}|${day}`;
      if (writtenDays.has(key)) continue;
      await setDay(owner, repo, day, [], false);
      filled++;
    }
  }

  // Surface imported repos in the picker's recent list so the user can
  // click through to them without retyping the slug.
  const slugs: string[] = [];
  for (const rangeKey of ranges.keys()) {
    const parts = rangeKey.split("|");
    const owner = parts[0];
    const repo = parts[1];
    if (owner && repo) slugs.push(`${owner}/${repo}`);
  }
  const reposAdded = saveRecentRepos(slugs);

  return { imported, days: grouped.size + filled, skipped, reposAdded };
}

/** Enumerate ISO days (YYYY-MM-DD) inclusively between `start` and `end`. */
function enumerateIsoDays(start: string, end: string): string[] {
  const days: string[] = [];
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return [start];
  }
  let cursor = startDate.getTime();
  const endMs = endDate.getTime();
  while (cursor <= endMs) {
    days.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += 24 * 60 * 60 * 1000;
  }
  return days;
}
